import { Button, cn, Separator } from "@monbits/ui";
import { formatDistanceToNowStrict } from "date-fns";
import { Copy, Send } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import OrderStatusLabel from "src/components/order-status/order-status";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import orderQueriers from "src/queries/order-queries";
import type { Order } from "src/types";
import { CURRENCY_SYMBOL } from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";
import { errorMessage } from "src/utils/error-message";
import { formatCryptoAmount, splitTicker } from "src/utils/format";

interface OrderDescCardProps {
	order: Order;
	onCopy: (title: string, description: string) => void;
}

const useRelativeTime = (target: Date, active: boolean): string => {
	const [, setTick] = React.useState(0);
	React.useEffect(() => {
		if (!active) return;
		const id = setInterval(() => setTick((t) => t + 1), 1000);
		return () => clearInterval(id);
	}, [active]);
	return formatDistanceToNowStrict(target, { addSuffix: true });
};

const OrderDescCard: React.FC<OrderDescCardProps> = ({ order, onCopy }) => {
	const triggerWebhook = orderQueriers.triggerOrderWebhook();

	const copyText = (text: string, _: string) => {
		copyToClipboard(text, onCopy);
	};

	const handleTriggerWebhook = async () => {
		try {
			await triggerWebhook.mutateAsync(order.id);
			toast.success(
				"Webhook triggered successfully! Check your webhook endpoint.",
			);
		} catch (error) {
			toast.error(errorMessage(error, "Failed to trigger webhook"));
		}
	};

	const expiresAt = React.useMemo(
		() => new Date(order.expiresAt),
		[order.expiresAt],
	);
	const isExpired = expiresAt < new Date();
	const isPending = order.status === "pending";
	const relativeExpiry = useRelativeTime(expiresAt, isPending && !isExpired);
	const token = splitTicker(order.cryptoCurrency).token ?? order.cryptoCurrency;
	const formattedAmount = formatCryptoAmount(order.cryptoAmount, {
		ticker: order.cryptoCurrency,
	});

	const isPaid = order.status === "paid";
	const isPartial = order.status === "partially_paid";

	return (
		<div className="space-y-8">
			{/* Payment instruction — the actionable hero. Status sits inline
			    with the "Amount due" label so state and amount read as one
			    unit (paid order = 0 owed; pending = this is what to send). */}
			<div className="space-y-5">
				<div className="space-y-1.5">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<p className="text-xs text-muted-foreground">Amount due</p>
							<OrderStatusLabel status={order.status} />
						</div>
						{isPending &&
							(isExpired ? (
								<StatusBadge type="error">Expired</StatusBadge>
							) : (
								<span className="text-xs text-muted-foreground tabular-nums">
									Expires {relativeExpiry}
								</span>
							))}
					</div>
					<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
						<span className="text-3xl font-semibold leading-none tracking-tight tabular-nums font-mono">
							{formattedAmount}
						</span>
						<span className="text-lg font-medium text-muted-foreground">
							{token}
						</span>
					</div>
					<p className="text-xs text-muted-foreground tabular-nums">
						on{" "}
						<span className="font-medium text-foreground">
							{order.cryptoBlockchain.toUpperCase()}
						</span>{" "}
						·{" "}
						<span className="font-medium text-foreground">
							{CURRENCY_SYMBOL[order.currency]}
							{order.price} {order.currency}
						</span>
					</p>
				</div>

				<button
					type="button"
					aria-label="Copy payment address"
					className="group w-full rounded-lg border border-border bg-muted/60 p-3 text-left transition-colors hover:bg-muted"
					onClick={() => copyText(order.paymentAddress, "Payment Address")}
				>
					<div className="space-y-1.5">
						<p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
							Send to
						</p>
						<div className="flex items-start gap-3">
							<span className="flex-1 text-sm font-mono break-all leading-relaxed">
								{order.paymentAddress}
							</span>
							<Copy className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
						</div>
					</div>
				</button>
			</div>

			{/* Settlement state — only when the order has actually moved.
			    Tinted block with semantic-status colors makes it read as
			    "completed/in-progress" rather than just another data row. */}
			{(isPaid || isPartial) && order.receivedAmount && (
				<div
					className={cn(
						"rounded-lg border p-3",
						isPartial
							? "border-(--warning-border) bg-(--warning-bg)"
							: "border-(--success-border) bg-(--success-bg)",
					)}
				>
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<span
								className={cn(
									"text-xs font-medium uppercase tracking-wider",
									isPartial ? "text-(--warning-fg)" : "text-(--success-fg)",
								)}
							>
								{isPartial ? "Partially received" : "Received"}
							</span>
						</div>
						<span
							className={cn(
								"font-mono text-sm font-semibold tabular-nums",
								isPartial ? "text-(--warning-fg)" : "text-(--success-fg)",
							)}
						>
							{formatCryptoAmount(order.receivedAmount, {
								ticker: order.cryptoCurrency,
							})}{" "}
							{token}
						</span>
					</div>
				</div>
			)}

			<Separator />

			{/* Metadata — definition list. Tighter grid, smaller type, sits
			    quietly below the actionable info. */}
			<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
				<dt className="text-muted-foreground">Order ID</dt>
				<dd className="min-w-0">
					<button
						type="button"
						aria-label="Copy order ID"
						className="group ml-auto flex items-center gap-2"
						onClick={() => copyText(order.id, "Order ID")}
					>
						<span className="truncate font-mono text-xs">{order.id}</span>
						<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
					</button>
				</dd>

				<dt className="text-muted-foreground">Created</dt>
				<dd className="text-right">
					<TimeLabel time={order.createdAt} />
				</dd>

				<dt className="text-muted-foreground">Expires</dt>
				<dd className="text-right">
					<TimeLabel time={order.expiresAt} />
				</dd>
			</dl>

			{/* Description — wrapped in a labeled card so it reads as
			    "merchant context for this order" instead of orphan text. */}
			{order.description && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Description
					</p>
					<p className="text-sm leading-relaxed">{order.description}</p>
				</div>
			)}

			{/* Actions — admin/support actions live here, clearly labeled. */}
			<Separator />
			<div className="space-y-3">
				<p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
					Actions
				</p>
				<div className="flex items-start justify-between gap-3">
					<div className="space-y-0.5">
						<p className="text-sm font-medium">Resend webhook</p>
						<p className="text-xs text-muted-foreground">
							Sends a fresh status dispatch to your configured endpoint.
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={handleTriggerWebhook}
						disabled={triggerWebhook.isPending}
						className="flex-shrink-0"
					>
						{triggerWebhook.isPending ? (
							<>
								<span className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
								Sending…
							</>
						) : (
							<>
								<Send className="mr-2 h-3.5 w-3.5" />
								Resend
							</>
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default OrderDescCard;
